// /trigger-builder — Event Builder (super admin only): conversational AI that
// creates NEW trigger events for the Automation tool's dropdown. Events carry
// no actions — rules are attached in the Automation tool.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Send, Lock, Trash2, FlaskConical, Play, RotateCcw,
  CalendarClock, Filter, CheckCircle2, AlertTriangle, Zap, ArrowRight,
} from "lucide-react";

const OP_LABELS = {
  equals: "=", not_equals: "≠", contains: "contains", is_empty: "is empty", not_empty: "is set",
  gt: ">", gte: "≥", lt: "<", lte: "≤", is_true: "is true", is_false: "is false",
  older_than_days: "older than (days)", within_next_days: "within next (days)",
};

const newSession = () => `eb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DraftCard = ({ draft, valid, errors, onSave, saving, saved }) => (
  <Card className={`rounded-sm p-3 mt-2 border ${valid ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/50"}`}
    data-testid="event-draft-card">
    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
      {valid ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-600" />}
      <span className="text-xs font-semibold text-slate-900">{draft.name}</span>
      <Badge className="rounded-sm font-normal text-[10px] bg-white border border-slate-200 text-slate-600">
        {draft.kind === "schedule" ? "Scheduled" : "Condition"}
      </Badge>
      {draft.entity && <Badge className="rounded-sm font-normal text-[10px] bg-white border border-slate-200 text-slate-600">{draft.entity}</Badge>}
      {draft.kind === "condition" && (
        <Badge className="rounded-sm font-normal text-[10px] bg-white border border-slate-200 text-slate-500">
          {draft.apply_to_existing ? "includes existing matches" : "new records only"}
        </Badge>
      )}
    </div>
    {draft.summary && <p className="text-xs text-slate-700">{draft.summary}</p>}
    {(draft.conditions || []).length > 0 && (
      <div className="flex flex-wrap gap-1 mt-1.5">
        {draft.conditions.map((c, i) => (
          <Badge key={i} className="rounded-sm font-normal text-[10px] bg-cyan-50 text-cyan-800 border border-cyan-200">
            {c.field} {OP_LABELS[c.op] || c.op.replace(/_/g, " ")} {c.value ?? ""}
          </Badge>
        ))}
      </div>
    )}
    {draft.schedule && (
      <Badge className="rounded-sm font-normal text-[10px] bg-violet-50 text-violet-700 border border-violet-200 mt-1.5">
        {draft.schedule.freq} @ {String(draft.schedule.hour).padStart(2, "0")}:{String(draft.schedule.minute).padStart(2, "0")} Sydney
      </Badge>
    )}
    {errors?.length > 0 && (
      <div className="text-[11px] text-amber-800 mt-1.5" data-testid="draft-errors">
        {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
      </div>
    )}
    {valid && !saved && (
      <Button size="sm" onClick={onSave} disabled={saving}
        className="rounded-sm h-7 mt-2 bg-emerald-700 hover:bg-emerald-800 text-[11px]"
        data-testid="save-event-btn">
        {saving ? <Loader2 size={11} className="animate-spin mr-1" /> : <CheckCircle2 size={11} className="mr-1" />}
        Save event — add to Automation tool
      </Button>
    )}
    {saved && (
      <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 mt-2" data-testid="event-saved-note">
        <CheckCircle2 size={11} /> Saved — now in the Automation tool's trigger list
      </div>
    )}
  </Card>
);

const EventRow = ({ ev, onToggle, onDelete, onTest, onRun, busy }) => {
  const [testResult, setTestResult] = useState(null);
  return (
    <div className="px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`event-row-${ev.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900">{ev.name}</span>
            <Badge className="rounded-sm font-normal text-[10px] bg-slate-50 text-slate-600 border border-slate-200">
              {ev.kind === "schedule" ? <CalendarClock size={10} className="mr-1" /> : <Filter size={10} className="mr-1" />}
              {ev.kind === "schedule" ? "Scheduled" : "Condition"}
            </Badge>
            {ev.entity && <Badge className="rounded-sm font-normal text-[10px] bg-cyan-50 text-cyan-700 border border-cyan-200">{ev.entity}</Badge>}
            <Badge className="rounded-sm font-normal text-[10px] bg-violet-50 text-violet-700 border border-violet-200">
              <Zap size={9} className="mr-1" /> {ev.rules_count || 0} rule{ev.rules_count === 1 ? "" : "s"}
            </Badge>
          </div>
          {ev.summary && <p className="text-xs text-slate-500 mt-0.5">{ev.summary}</p>}
          <p className="text-[10px] text-slate-400 mt-0.5">
            Fired {ev.fire_count || 0}× {ev.last_run_at ? `· last ${new Date(ev.last_run_at).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
          </p>
        </div>
        <Switch checked={ev.enabled !== false} onCheckedChange={(v) => onToggle(ev.id, v)}
          data-testid={`event-toggle-${ev.id}`} />
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <Button size="sm" variant="outline" className="rounded-sm h-6 text-[10px]" disabled={busy}
          onClick={async () => setTestResult(await onTest(ev.id))} data-testid={`event-test-${ev.id}`}>
          <FlaskConical size={10} className="mr-1" /> Test
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-6 text-[10px]" disabled={busy}
          onClick={() => onRun(ev.id)} data-testid={`event-run-${ev.id}`}>
          <Play size={10} className="mr-1" /> Fire now
        </Button>
        <Button size="sm" variant="ghost" className="rounded-sm h-6 text-[10px] text-rose-600 hover:bg-rose-50"
          onClick={() => onDelete(ev)} data-testid={`event-delete-${ev.id}`}>
          <Trash2 size={10} />
        </Button>
      </div>
      {testResult && (
        <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2 mt-1.5"
          data-testid={`event-test-result-${ev.id}`}>
          {testResult.next_fire && <div>⏰ Fires {testResult.next_fire}</div>}
          <div>Matching now: <b>{testResult.matching ?? 0}</b> · would fire <b>{testResult.would_fire ?? 0}</b> time(s) into <b>{testResult.rules_count ?? 0}</b> rule(s)</div>
          {(testResult.sample || []).length > 0 && <div className="text-slate-400 truncate">e.g. {testResult.sample.join(" · ")}</div>}
        </div>
      )}
    </div>
  );
};

export default function TriggerBuilder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState(newSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const isSuper = user?.role === "super_admin";

  const { data: events = [] } = useQuery({
    queryKey: ["custom-events"],
    queryFn: async () => (await api.get("/custom-events")).data,
    enabled: isSuper,
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, thinking]);

  if (!isSuper) {
    return (
      <div className="max-w-xl mx-auto p-10">
        <Card className="rounded-sm p-8 text-center" data-testid="trigger-builder-locked">
          <Lock className="mx-auto text-slate-300 mb-3" size={28} />
          <h2 className="text-sm font-semibold text-slate-900">Super admin only</h2>
          <p className="text-xs text-slate-500 mt-1">The Event Builder is restricted to the super admin account.</p>
        </Card>
      </div>
    );
  }

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setThinking(true);
    try {
      const { data } = await api.post("/custom-events/ai-chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "ai", text: data.reply, draft: data.draft, valid: data.valid, errors: data.errors }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: e?.response?.data?.detail || "Something went wrong — try again." }]);
    } finally {
      setThinking(false);
    }
  };

  const saveDraft = async (draft, msgIndex) => {
    setSaving(true);
    try {
      const { data } = await api.post("/custom-events", draft);
      const skipped = data.baseline_skipped
        ? ` ${data.baseline_skipped} already-matching record(s) were skipped — only new matches will fire.` : "";
      toast.success(`Event "${data.name}" added to the Automation tool`);
      setMessages((m) => m.map((msg, i) => (i === msgIndex ? { ...msg, saved: true } : msg)));
      setMessages((m) => [...m, { role: "ai", text: `✅ "${data.name}" is live — it now appears in the Automation tool's trigger dropdown under "Custom events". Open the Automation tool to attach actions (tasks, emails, notifications) to it.${skipped}` }]);
      qc.invalidateQueries({ queryKey: ["custom-events"] });
      qc.invalidateQueries({ queryKey: ["automation-catalog"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id, enabled) => {
    await api.patch(`/custom-events/${id}`, { enabled });
    qc.invalidateQueries({ queryKey: ["custom-events"] });
  };
  const del = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}"? Any automation rules using it will be switched off.`)) return;
    const { data } = await api.delete(`/custom-events/${ev.id}`);
    toast.success(data.rules_disabled ? `Event deleted — ${data.rules_disabled} rule(s) switched off` : "Event deleted");
    qc.invalidateQueries({ queryKey: ["custom-events"] });
    qc.invalidateQueries({ queryKey: ["automation-catalog"] });
  };
  const test = async (id) => {
    setBusy(true);
    try { return (await api.post(`/custom-events/${id}/test`)).data; }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); return null; }
    finally { setBusy(false); }
  };
  const runNow = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/custom-events/${id}/run-now`);
      toast.success(`Fired ${data.fired} time(s) — ${data.matching ?? 0} matching record(s). Rules using this event have run.`);
      qc.invalidateQueries({ queryKey: ["custom-events"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="trigger-builder-page">
      <header className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[2px] text-violet-600">Super Admin</div>
          <h1 className="font-display font-bold text-slate-900 text-3xl leading-tight mt-1">Event Builder</h1>
          <p className="text-xs text-slate-500 mt-1">
            Describe a new trigger event in plain English — once saved it appears in the Automation tool's
            trigger list, where you attach the actions.
          </p>
        </div>
        <Button variant="outline" onClick={() => nav("/automation")} className="rounded-sm h-9"
          data-testid="open-automation-btn">
          Open Automation tool <ArrowRight size={13} className="ml-1.5" />
        </Button>
      </header>

      <div className="grid lg:grid-cols-5 gap-5">
        <Card className="rounded-sm lg:col-span-3 flex flex-col h-[560px]">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-900">
              <Sparkles size={13} className="text-violet-600" /> Build a new event
            </div>
            <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[11px] text-slate-500"
              onClick={() => { setSessionId(newSession()); setMessages([]); }} data-testid="new-conversation-btn">
              <RotateCcw size={11} className="mr-1" /> New conversation
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-xs text-slate-400 space-y-2 pt-4" data-testid="chat-empty-state">
                <p className="font-medium text-slate-500">Try something like:</p>
                <p>“A quote has been sent for more than 5 days and still isn't accepted.”</p>
                <p>“A job hasn't received its deposit 7 days after being created.”</p>
                <p>“Every Friday at 3pm.” — then attach a weekly report email to it in the Automation tool.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className={`max-w-[85%] rounded-sm px-3 py-2 text-xs whitespace-pre-wrap ${
                  m.role === "user" ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-800"}`}>
                  {m.text}
                  {m.draft && <DraftCard draft={m.draft} valid={m.valid} errors={m.errors} saved={m.saved}
                    onSave={() => saveDraft(m.draft, i)} saving={saving} />}
                </div>
              </div>
            ))}
            {thinking && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={12} className="animate-spin" /> thinking…</div>}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-slate-100 p-3 flex items-end gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Describe the event you want to detect…" rows={2}
              className="rounded-sm text-xs resize-none" data-testid="chat-input" />
            <Button onClick={send} disabled={thinking || !input.trim()}
              className="rounded-sm h-9 bg-violet-700 hover:bg-violet-800" data-testid="chat-send-btn">
              <Send size={13} />
            </Button>
          </div>
        </Card>

        <div className="lg:col-span-2">
          <h2 className="text-sm font-semibold text-slate-900 mb-2">Your custom events</h2>
          <Card className="rounded-sm p-0 overflow-hidden">
            {events.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400" data-testid="events-empty">
                Nothing yet — build your first event in the chat. It'll show up in the Automation tool's trigger list.
              </div>
            ) : events.map((ev) => (
              <EventRow key={ev.id} ev={ev} onToggle={toggle} onDelete={del} onTest={test} onRun={runNow} busy={busy} />
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
